import { jsx } from 'react/jsx-runtime';
import { useState, useEffect } from 'react';
import { createGlobalStyle, css } from 'styled-components';
import { isInMobile } from '@fabric-app/compat';
import { useReferencedFn } from '@fabric-app/hooks/useReferencedFn';
import { useClientLayoutEffect } from '@fabric-app/hooks/useClientLayoutEffect';
import { detectMobile } from '@fabric-app/utils/detection';
import { mediaMobile } from '../constants/styled-utils.js';

const visualViewportToViewport = (visualViewport) => ({
  width: visualViewport.width,
  height: visualViewport.height,
  offsetLeft: visualViewport.offsetLeft,
  offsetTop: visualViewport.offsetTop,
  pageLeft: visualViewport.pageLeft,
  pageTop: visualViewport.pageTop,
  scale: visualViewport.scale,
  windowActualWidth: window.innerWidth,
  windowActualHeight: window.innerHeight,
  heightCrop: Math.max(Math.floor(window.innerHeight - visualViewport.height), 0)
});
function useVisualViewport(options = {}) {
  const disabled = options.disabled ?? false;
  const recalculateKey = options.recalculateKey ?? null;
  const [viewport, setViewport] = useState(() => {
    if (disabled || typeof window === "undefined") return;
    return window.visualViewport ? {
      ...visualViewportToViewport(window.visualViewport),
      height: Math.min(window.visualViewport.height, window.innerHeight),
      width: Math.min(window.visualViewport.width, window.innerWidth)
    } : void 0;
  });
  const updateViewportRef = useReferencedFn(() => {
    const newViewport = window.visualViewport ? {
      ...visualViewportToViewport(window.visualViewport),
      /**
      * bug, some mobile browsers report height 0 when keyboard is open
      */
      height: Math.min(window.visualViewport.height || 400, window.innerHeight),
      width: Math.min(window.visualViewport.width, window.innerWidth)
    } : void 0;
    setViewport(newViewport);
  });
  useEffect(() => {
    if (disabled || typeof window === "undefined") return;
    updateViewportRef.current();
  }, [
    recalculateKey,
    disabled,
    updateViewportRef
  ]);
  useClientLayoutEffect(() => {
    if (disabled || typeof window === "undefined") return;
    const updateViewport = updateViewportRef.current;
    window.visualViewport?.addEventListener("resize", updateViewport);
    window.addEventListener("resize", updateViewport);
    const resizeObserver = new ResizeObserver(updateViewport);
    resizeObserver.observe(document.documentElement);
    updateViewport();
    return () => {
      window.visualViewport?.removeEventListener("resize", updateViewport);
      window.removeEventListener("resize", updateViewport);
      resizeObserver.disconnect();
    };
  }, [
    disabled,
    updateViewportRef
  ]);
  return viewport;
}
const GlobalViewportStyle = createGlobalStyle([
  ``,
  ``
], ({ viewport }) => css([
  `:root{--viewport-width:`,
  `px;--viewport-height:`,
  `px;--viewport-offset-left:`,
  `px;--viewport-offset-top:`,
  `px;--viewport-page-left:`,
  `px;--viewport-page-top:`,
  `px;--viewport-scale:`,
  `;}`
], viewport.width, viewport.height, viewport.offsetLeft, viewport.offsetTop, viewport.pageLeft, viewport.pageTop, viewport.scale));
function useVisualViewportWithGlobalStyle() {
  const isMobileWeb = !isInMobile() && detectMobile(true);
  const viewport = useVisualViewport({
    disabled: !isMobileWeb
  });
  const isViewportAvailable = viewport !== void 0 && !isInMobile() && detectMobile(true);
  useClientLayoutEffect(() => {
    if (!isViewportAvailable) return;
    document.documentElement.setAttribute("data-viewport", "true");
    return () => {
      document.documentElement.removeAttribute("data-viewport");
    };
  }, [
    isViewportAvailable
  ]);
  if (!isViewportAvailable) return null;
  return /* @__PURE__ */ jsx(GlobalViewportStyle, {
    viewport
  });
}
const handleViewportCSS = css([
  ``,
  `{html[data-viewport='true'] &{transition:none;transform:translate(0,var(--viewport-page-top));height:var(--viewport-height) !important;max-height:var(--viewport-height) !important;min-height:var(--viewport-height) !important;}}`
], mediaMobile);

export { useVisualViewport as default, handleViewportCSS, useVisualViewportWithGlobalStyle };
//# sourceMappingURL=useVisualViewport.js.map
